import Badge from 'components/badge/badge';
import TaskCheckbox from 'components/tasks/TaskCheckbox';
import { ROW_CASCADE_CLASS, rowCascadeDelay } from 'utils/surfaceClasses';

// The gesture under way on a row: `done` (ticked, lit until the api answers),
// `leave` (it folds away) or `enter` (it comes back, the tick undone).
const MOTION_CLASSES = {
  done: 'bg-green-50',
  leave: 'overflow-hidden bg-green-50 motion-safe:animate-taskRowLeave',
  enter: 'overflow-hidden bg-white motion-safe:animate-taskRowEnter',
};

// A dashed line of 8px dashes under each row but the last.
const DASHED_SEPARATOR =
  'bg-[linear-gradient(to_right,#e5e7eb_8px,transparent_8px)] bg-[length:14px_1px] bg-left-bottom bg-repeat-x last:bg-none';

/**
 * A row of « Tâches du jour »: its checkbox, the icon of its type, its title
 * and what it concerns, then its time. A late task shows its due day in a red
 * badge; a time already passed turns red. Without `onOpen`, the row is not a
 * button. A box that cannot be ticked here is locked: `lockedLabel` says why.
 * `cascadeIndex`: its place in the rows' cascade (« Mes tâches »'s), while the
 * list comes in; none once it is there.
 */
const TaskRow = ({ item, motion, cascadeIndex, checkLabel, lockedLabel, onToggle, onOpen }) => {
  const Icon = item.icon;
  const open = onOpen ? () => onOpen(item) : undefined;
  const cascades = cascadeIndex !== undefined && !motion;
  const rest = `${open ? 'cursor-pointer bg-white hover:bg-gray-50' : 'bg-white'} ${cascades ? ROW_CASCADE_CLASS : ''}`;
  return (
    <div
      data-flip={item.key}
      role={open ? 'button' : undefined}
      tabIndex={open ? 0 : undefined}
      onClick={open}
      onKeyDown={
        open
          ? (event) => {
              if (event.key === 'Enter' && event.target === event.currentTarget) open();
            }
          : undefined
      }
      className={`flex h-16 items-center gap-3 px-4 py-3 transition-colors duration-300 ${DASHED_SEPARATOR} ${
        MOTION_CLASSES[motion] ?? rest
      }`}
      style={cascades ? rowCascadeDelay(cascadeIndex) : undefined}
    >
      <TaskCheckbox
        checked={item.done}
        disabled={!item.toggleable}
        disabledReason={lockedLabel}
        label={checkLabel}
        onToggle={() => onToggle(item)}
      />
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-900">
        <Icon wh={16} color="currentColor" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
        <span
          className={`truncate text-sm font-medium leading-5 transition-colors duration-300 ${
            item.done ? 'text-gray-400' : 'text-gray-900'
          }`}
        >
          {item.title}
        </span>
        <span className="truncate text-xs leading-4 text-gray-500">{item.subtitle}</span>
      </span>
      {item.late ? (
        <span className="shrink-0">
          <Badge type="ALERT">{item.right}</Badge>
        </span>
      ) : (
        <span
          className={`shrink-0 text-xs font-medium ${
            item.passed ? 'text-red-600' : item.done ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          {item.right}
        </span>
      )}
    </div>
  );
};

export default TaskRow;
